import React, { useState } from 'react';
import { UiComponent } from 'components';
import { OrganizationQuery } from 'queries';
import { IOrganization } from 'types';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useModal from 'hooks/useModal';

interface Props {
  organizations: IOrganization[];
  onSubmit: (organizations: IOrganization[]) => void;
}

const ITEM_TYPE = 'ORG';

function DraggableOrgItem({
  org,
  index,
  selectedOrgs,
  setSelectedOrgs,
}: {
  org: IOrganization;
  index: number;
  selectedOrgs: IOrganization[];
  setSelectedOrgs: (orgs: IOrganization[]) => void;
}) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ITEM_TYPE,
      item: { org, index },
      collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    }),
    [org, index],
  );

  const [, drop] = useDrop(
    () => ({
      accept: ITEM_TYPE,
      drop: (item: { org: IOrganization; index: number }) => {
        if (item.index === index) return;
        const next = [...selectedOrgs];
        const dragged = next[item.index];
        if (!dragged) return;
        next.splice(item.index, 1);
        next.splice(index, 0, dragged);
        setSelectedOrgs(next);
      },
    }),
    [index, selectedOrgs, setSelectedOrgs],
  );

  return (
    <div
      ref={(node) => {
        drag(node);
        drop(node);
      }}
      css={{
        padding: '8px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        marginBottom: '4px',
        cursor: 'move',
        backgroundColor: isDragging ? '#f0f0f0' : 'white',
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <p css={{ margin: 0 }}>
        <b css={{ marginRight: '4px' }}>[{org.id}]</b>
        {org.displayName}
      </p>
      <UiComponent.Button
        variant="contained"
        color="primary"
        onClick={() =>
          setSelectedOrgs(selectedOrgs.filter((o) => o.id !== org.id))
        }
      >
        제거
      </UiComponent.Button>
    </div>
  );
}

function SelectOrganizationModal({ organizations, onSubmit }: Props) {
  const { handleClose } = useModal();
  const { data: allOrgs } = OrganizationQuery.useGetAll();
  const [keyword, setKeyword] = useState('');
  const [selectedOrgs, setSelectedOrgs] = useState<IOrganization[]>(
    organizations.filter((o): o is IOrganization => !!o),
  );

  const filtered = (allOrgs || []).filter(
    (o) =>
      !keyword ||
      o.displayName.toLowerCase().includes(keyword.toLowerCase()) ||
      o.name.toLowerCase().includes(keyword.toLowerCase()),
  );

  const isSelected = (o: IOrganization) =>
    selectedOrgs.some((s) => s.id === o.id);

  const handleSelect = (o: IOrganization) => {
    if (isSelected(o)) return;
    setSelectedOrgs([...selectedOrgs, o]);
  };

  const handleSave = () => {
    onSubmit(selectedOrgs);
    handleClose();
  };

  return (
    <div css={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
      {/* 검색 패널 */}
      <div css={{ width: '300px' }}>
        <div css={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
          <input
            css={{
              flex: 1,
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
            }}
            placeholder="조직명 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
        <ul
          css={{
            overflowY: 'scroll',
            maxHeight: '320px',
            margin: 0,
            padding: 0,
            listStyle: 'none',
          }}
        >
          {filtered.map((o) => (
            <li
              key={o.id}
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px',
                borderBottom: '1px solid #ddd',
              }}
            >
              <p css={{ margin: 0 }}>
                <b>[{o.id}]</b> {o.displayName}
              </p>
              {isSelected(o) ? (
                <UiComponent.Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    setSelectedOrgs(selectedOrgs.filter((s) => s.id !== o.id))
                  }
                >
                  제거
                </UiComponent.Button>
              ) : (
                <UiComponent.Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSelect(o)}
                >
                  선택
                </UiComponent.Button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* 선택된 조직 패널 (DnD) */}
      <div css={{ width: '300px' }}>
        <h3 css={{ margin: '0 0 8px' }}>선택된 조직 (순서 드래그 가능)</h3>
        <DndProvider backend={HTML5Backend}>
          <div
            css={{
              border: '1px solid #eee',
              borderRadius: '4px',
              padding: '8px',
              overflowY: 'scroll',
              height: '320px',
            }}
          >
            {selectedOrgs.length === 0 ? (
              <p css={{ textAlign: 'center', color: '#999', margin: '20px 0' }}>
                선택된 조직이 없습니다.
              </p>
            ) : (
              selectedOrgs.map((o, idx) => (
                <DraggableOrgItem
                  key={o.id}
                  org={o}
                  index={idx}
                  selectedOrgs={selectedOrgs}
                  setSelectedOrgs={setSelectedOrgs}
                />
              ))
            )}
          </div>
        </DndProvider>
        <div
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '8px',
          }}
        >
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            저장하기
          </UiComponent.Button>
        </div>
      </div>
    </div>
  );
}

export default SelectOrganizationModal;
